import React, { useMemo, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CheckIcon } from '../icons';
import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';

const money = (n: number) => '£' + n.toLocaleString('en-GB');
const BLADE_COLORS = [V.brandBlue, V.brandCyan, V.brandViolet, V.brandTeal];
const MILL_DIRECTIONS = ['right', 'down', 'left', 'complete'] as const;

const PricingMill: React.FC<{ planIndex: number }> = ({ planIndex }) => (
  <svg
    data-pricing-mill={MILL_DIRECTIONS[planIndex]}
    aria-hidden="true"
    width="72"
    height="72"
    viewBox="-68 -68 136 136"
    style={{ flex: 'none' }}
  >
    <g transform="rotate(70)">
      {BLADE_COLORS.map((color, index) => {
        const open = planIndex < 3 && index === planIndex;
        return (
          <g key={color} transform={`rotate(${index * 90})`} opacity={open || planIndex === 3 ? 1 : 0.25}>
            <g transform={open ? 'translate(4 -10) scale(1.16)' : undefined}>
              <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={color} />
              <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
            </g>
          </g>
        );
      })}
    </g>
  </svg>
);

interface Plan {
  name: string;
  tagline: string;
  features: string[];
  popular: boolean;
  price: string;
  per: string;
  unit: string;
  cta: string;
  to: string;
  btnBg: string;
  btnColor: string;
  btnBorder: string;
}

const buildPlan = (name: string, perFlat: number, fixed: number, tagline: string, features: string[], popular: boolean, flats: number, yearly: boolean): Plan => {
  const over = flats > 15;
  let price: string, per: string, unit: string, cta = 'Start free trial', to = '/register';
  let btnBg: string = C.white, btnColor: string = C.ink, btnBorder = `1px solid ${C.hairline}`;

  if (perFlat === 0 && fixed === 0) {
    price = '£0';
    per = 'for 1 week';
    unit = 'Up to 10 flats · both agents';
    cta = 'Start free';
  } else if (over) {
    price = "Let's talk";
    per = '';
    unit = '15+ flats — custom';
    cta = 'Book a call';
    to = '/book-a-call';
  } else {
    const monthly = perFlat * flats + fixed;
    const total = yearly ? monthly * 10 : monthly;
    price = money(total);
    per = yearly ? '/yr' : '/mo';
    unit =
      (perFlat && fixed ? `£${perFlat} / flat + £${fixed} / mo` : fixed ? `£${fixed} / mo flat fee` : `£${perFlat} / flat / mo`) +
      (yearly ? ' · billed yearly' : '');
  }
  if (popular) {
    btnBg = C.navy;
    btnColor = C.white;
    btnBorder = 'none';
  }

  return { name, tagline, features, popular, price, per, unit, cta, to, btnBg, btnColor, btnBorder };
};

const PLAN_DEFS: Array<[string, number, number, string, string[], boolean]> = [
  ['Free', 0, 0, 'Try both agents free for 1 week.', ['Up to 10 flats — Certificates and Accounting', 'MillOS books your renewals', 'Open Banking rent tracking', 'Quarterly figures prepared', 'No payment needed. No card required.'], false],
  ['Certificates', 6, 0, 'Beyond tracking. Into action.', ['Reads certificates and tracks expiry dates', 'Coordinates renewals with engineers and tenants', 'Gas, EPC, EICR, PAT and fire tracked', 'New certificates filed against the right property', 'Audit-ready evidence pack per property'], false],
  ['Accounting', 0, 40, 'Beyond recording. Into preparation.', ['Matches rent payments to the right flat', 'Organises invoices and expenses by property', 'Prepares quarterly figures for Making Tax Digital', 'Figures ready to share with your accountant', 'One fixed accounting fee for your portfolio'], false],
  ['Complete', 6, 40, 'Both agents, one bill.', ['Both agents — Certificates and Accounting', 'Renewals and cash on one dashboard', '£6 per flat plus one £40 accounting fee', 'Priority support, UK-based', 'One bill for the whole portfolio'], true],
];

const MillosPricingHero: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [flats, setFlats] = useState(1);
  const [expandedPlans, setExpandedPlans] = useState<string[]>([]);
  const isMobile = useMediaQuery('(max-width:639px)');
  const yearly = billing === 'yearly';
  const over = flats > 15;
  const plans = useMemo(
    () => PLAN_DEFS.map(([name, perFlat, fixed, tagline, features, popular]) => buildPlan(name, perFlat, fixed, tagline, features, popular, flats, yearly)),
    [flats, yearly]
  );
  const togglePlan = (name: string) => {
    setExpandedPlans(current => current.includes(name) ? current.filter(plan => plan !== name) : [...current, name]);
  };

  return (
    <Box component="section" aria-label="Pricing plans" sx={{ padding: '12px' }}>
      <Box sx={{ maxWidth: 1392, margin: '0 auto', background: C.white, borderRadius: V.borderRadius32px, padding: { xs: '96px 12px 40px', sm: '96px 24px 40px', md: '112px 24px 48px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center', color: C.ink }}>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: isMobile ? 'clamp(2rem,8.5vw,2.5rem)' : 'clamp(2.2rem,4vw,3.4rem)', lineHeight: 1.06, letterSpacing: '-0.035em', color: C.ink }}>
            Pricing that scales <Box component="em" sx={{ fontStyle: 'italic' }}>with you</Box>
          </Box>
          <Box component="p" sx={{ color: C.body }}>Choose Certificates, Accounting, or both. AI agents that act, not just another checklist.</Box>
          <Box sx={{ display: 'flex', flexWrap: isMobile ? 'nowrap' : 'wrap', justifyContent: 'center', alignItems: 'center', gap: isMobile ? '8px' : '12px', maxWidth: isMobile ? 430 : undefined, margin: '20px auto 0' }}>
            <Box sx={{ display: 'inline-flex', gap: isMobile ? '2px' : 0, background: C.panel, border: `1px solid ${C.hairline}`, borderRadius: V.borderRadius32px, padding: '4px' }}>
              <Box
                component="button"
                type="button"
                aria-pressed={!yearly}
                onClick={() => setBilling('monthly')}
                sx={{ whiteSpace: 'nowrap', flex: 'none', border: 'none', cursor: 'pointer', fontFamily: F.body, fontSize: isMobile ? 12 : 13.5, fontWeight: 600, borderRadius: '24px', padding: isMobile ? '6px 8px' : '9px 18px', minHeight: isMobile ? 44 : undefined, background: yearly ? 'transparent' : C.navy, color: yearly ? C.body : V.onDark }}
              >
                Monthly
              </Box>
              <Box
                component="button"
                type="button"
                aria-pressed={yearly}
                aria-label="Yearly · save 17%"
                onClick={() => setBilling('yearly')}
                sx={{ whiteSpace: 'nowrap', flex: 'none', border: 'none', cursor: 'pointer', fontFamily: F.body, fontSize: isMobile ? 12 : 13.5, fontWeight: 600, borderRadius: '24px', padding: isMobile ? '6px 8px' : '9px 18px', minHeight: isMobile ? 44 : undefined, background: yearly ? C.navy : 'transparent', color: yearly ? V.onDark : C.body }}
              >
                {isMobile ? <>Yearly<Box component="span" sx={{ display: 'block', fontSize: 9, lineHeight: 1.2, marginTop: '2px' }}>save 17%</Box></> : 'Yearly · save 17%'}
              </Box>
            </Box>
            <Box sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center', gap: isMobile ? '2px' : '4px', background: C.white, borderRadius: '32px', padding: isMobile ? '4px 0' : '6px 6px 6px 8px' }}>
              <Box sx={{ fontSize: isMobile ? 11 : 13.5, color: C.body, fontWeight: 500 }}>Flats</Box>
              <Box
                component="button"
                type="button"
                aria-label="Remove a flat"
                disabled={flats === 1}
                onClick={() => setFlats(n => Math.max(1, n - 1))}
                sx={{ width: 34, height: 34, borderRadius: '24px', border: `1px solid ${C.hairline}`, background: C.panel, cursor: 'pointer', fontSize: 19, color: C.accent, lineHeight: 1 }}
              >
                −
              </Box>
              <Box sx={{ minWidth: isMobile ? 24 : 32, textAlign: 'center', fontFamily: F.display, fontSize: isMobile ? 26 : 32, lineHeight: 1, transform: 'translateY(-4px)', color: C.ink }}>{over ? '15+' : String(flats)}</Box>
              <Box
                component="button"
                type="button"
                aria-label="Add a flat"
                disabled={flats === 51}
                onClick={() => setFlats(n => Math.min(51, n + 1))}
                sx={{ width: 34, height: 34, borderRadius: '24px', border: 'none', background: C.accent, cursor: 'pointer', fontSize: 19, color: C.white, lineHeight: 1 }}
              >
                +
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ margin: '44px auto 0', display: 'grid', gridTemplateColumns: '1fr', '@media (min-width:640px)': { gridTemplateColumns: 'repeat(2,minmax(0,1fr))' }, '@media (min-width:1024px)': { gridTemplateColumns: 'repeat(4,minmax(0,1fr))' }, alignItems: isMobile ? 'start' : 'stretch', gap: '16px' }}>
          {plans.map((plan, index) => {
            const expanded = !isMobile || expandedPlans.includes(plan.name);
            const detailsId = `pricing-plan-${plan.name.toLowerCase()}`;
            return (
              <Box
                component="article"
                aria-labelledby={`${detailsId}-title`}
                key={plan.name}
                onClick={isMobile ? (event: React.MouseEvent<HTMLElement>) => {
                  if (event.target instanceof Element && event.target.closest('a, button')) return;
                  togglePlan(plan.name);
                } : undefined}
                sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px', background: C.white, borderRadius: '28px', padding: { xs: '24px 20px', lg: '26px 22px' }, position: 'relative', cursor: isMobile ? 'pointer' : 'default', border: plan.popular ? `2px solid ${C.accent}` : `1px solid ${C.hairline}` }}
              >
                {plan.popular && (
                  <Box sx={{ position: 'absolute', top: -11, left: 22, background: C.millosMint, color: C.navy, borderRadius: '32px', padding: '5px 12px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase' }}>
                    Both agents
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                  <Box component="h2" id={`${detailsId}-title`} sx={{ fontFamily: F.ui, fontWeight: 600, fontSize: { xs: 24, md: 22, lg: 24 }, margin: 0 }}>{plan.name}</Box>
                  <PricingMill planIndex={index} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px' }}>
                    <Box sx={{ fontFamily: F.display, fontSize: 38, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{plan.price}</Box>
                    <Box sx={{ fontFamily: F.display, fontSize: 15, color: C.body }}>{plan.per}</Box>
                  </Box>
                  <Box sx={{ fontSize: 12.5, lineHeight: 1.5, color: C.accent, fontWeight: 600, marginTop: '6px' }}>{plan.unit}</Box>
                </Box>
                {!isMobile && <Box component="p" sx={{ color: C.body, fontSize: 13, margin: 0, minHeight: '40px' }}>{plan.tagline}</Box>}
                <Box component="ul" aria-label={`${plan.name} included features`} sx={{ display: 'flex', flexDirection: 'column', gap: '11px', listStyle: 'none', padding: 0, margin: 0 }}>
                  {plan.features.slice(0, 2).map(feature => (
                    <Box component="li" key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 13, color: C.body }}>
                      <Box component="span" sx={{ flex: 'none', marginTop: '1px' }}><CheckIcon size={18} /></Box>
                      {feature}
                    </Box>
                  ))}
                </Box>
                <Box id={detailsId} hidden={!expanded} sx={{ display: expanded ? 'flex' : 'none', flexDirection: 'column', gap: '16px', flex: 1 }}>
                  {isMobile && <Box component="p" sx={{ color: C.body, fontSize: 13, margin: 0 }}>{plan.tagline}</Box>}
                  <Box component="ul" aria-label={`${plan.name} additional features`} sx={{ display: 'flex', flexDirection: 'column', gap: '11px', listStyle: 'none', padding: 0, margin: 0 }}>
                    {plan.features.slice(2).map(feature => (
                      <Box component="li" key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 13, color: C.body }}>
                        <Box component="span" sx={{ flex: 'none', marginTop: '1px' }}><CheckIcon size={18} /></Box>
                        {feature}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box
                  component={RouterLink}
                  to={plan.to}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: plan.btnBg, color: plan.btnColor, borderRadius: '32px', padding: '12px 16px', fontSize: 15, marginTop: 'auto', border: plan.btnBorder, '&:hover': { background: C.millosBlue, color: C.white } }}
                >
                  {plan.cta}
                </Box>
                {isMobile && (
                  <Box
                    component="button"
                    type="button"
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${plan.name} features`}
                    aria-expanded={expanded}
                    aria-controls={detailsId}
                    onClick={() => togglePlan(plan.name)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: 44, border: 'none', borderRadius: '24px', background: C.panel, color: C.accent, cursor: 'pointer', fontFamily: F.body, fontSize: 13, fontWeight: 600 }}
                  >
                    {expanded ? 'Show less' : 'Show all features'}
                    <KeyboardArrowDownIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ textAlign: 'center', marginTop: '28px', fontSize: 13.5, color: C.body }}>
          Managing over 15 flats?{' '}
          <Box component={RouterLink} to="/book-a-call" sx={{ color: V.tealText, fontWeight: 600, textDecoration: 'underline' }}>Book a demo →</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MillosPricingHero;
